import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/ConnectWise.css";
import connectwiseLogo from "../../assets/images/partners/connectwise.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const pipelineSteps = [
  { icon: "🔍", title: "Monitor", desc: "Continuous RMM endpoint health monitoring across all managed devices and environments." },
  { icon: "🔔", title: "Alert", desc: "Intelligent alert generation with severity scoring and noise reduction through automation." },
  { icon: "🎫", title: "Ticket", desc: "Auto-ticket creation in PSA with correct assignment, priority, and SLA clock start." },
  { icon: "⚙️", title: "Automate", desc: "Run automated remediation scripts before escalating to a human technician." },
  { icon: "✅", title: "Resolve", desc: "Technician resolution with time tracked to contract, client notified automatically." },
];

const psaFeatures = [
  { icon: "🎫", title: "Service Ticket Management", desc: "Full ticket lifecycle from creation through resolution — with SLA tracking, priority queues, and customer-facing portal status updates." },
  { icon: "📋", title: "Project Management", desc: "Project templates, resource planning, milestone tracking, and profitability reporting — managing complex client implementations alongside day-to-day service." },
  { icon: "💰", title: "Contract & Billing", desc: "Flexible contract models (retainer, block hours, per-device) with automated invoice generation and time entry validation against contract terms." },
  { icon: "📊", title: "Business Intelligence", desc: "Real-time dashboards tracking technician utilization, service profitability, SLA performance, and revenue per client for operational excellence." },
  { icon: "🤝", title: "Client Portal", desc: "Branded self-service portal where clients submit tickets, check status, approve quotes, and view service history without contacting the help desk." },
];

const rmmCaps = [
  { icon: "🖥️", title: "Endpoint Monitoring", desc: "Real-time health monitoring for Windows, macOS, Linux, and network devices with 250+ out-of-box monitors." },
  { icon: "🔧", title: "Remote Control", desc: "Secure unattended and attended remote sessions with session recording, multi-screen support, and zero-trust access controls." },
  { icon: "🔄", title: "Patch Management", desc: "Automated patch deployment for OS and third-party applications — with ring-based rollout, approval workflows, and compliance reporting." },
  { icon: "📜", title: "Script Automation", desc: "Hundreds of pre-built automation scripts plus custom PowerShell, Bash, and Python — scheduled or triggered by monitoring conditions." },
];

const autoCards = [
  { icon: "🤖", title: "ConnectWise Automate", desc: "Industry-leading RMM and automation platform — script-based automation for routine IT tasks, patch management, software deployment, and remediation workflows across thousands of endpoints.", tags: ["Scripting Engine", "250+ Monitors", "Scheduled Tasks"] },
  { icon: "⚡", title: "ConnectWise Manage (PSA)", desc: "The world's most-deployed PSA for MSPs — managing tickets, projects, billing, procurement, and client relationships through one integrated business management platform.", tags: ["Ticket Lifecycle", "Time Tracking", "Contract Billing"] },
  { icon: "🔐", title: "ConnectWise SIEM / MDR", desc: "Co-managed SIEM and managed detection and response services — extending your security operations with 24/7 SOC monitoring, threat hunting, and incident response.", tags: ["24/7 SOC", "Threat Hunting", "MDR"] },
  { icon: "🛡️", title: "ConnectWise Cybersecurity", desc: "Integrated security platform combining endpoint protection, DNS filtering, dark web monitoring, and security awareness training in a single MSP-managed stack.", tags: ["EDR", "DNS Security", "Phishing Sim"] },
  { icon: "💬", title: "ConnectWise Command", desc: "Cloud-based RMM with built-in NOC services — remote monitoring, alerting, and technician dispatch with ConnectWise-operated NOC support for after-hours coverage.", tags: ["Cloud RMM", "NOC Services", "24/7 Monitoring"] },
  { icon: "📈", title: "ConnectWise CPQ", desc: "Configure, price, quote platform integrating vendor catalog data for instant hardware and software quoting — eliminating manual data entry and accelerating sales cycles.", tags: ["Vendor Catalog", "Quote Automation", "Approval Workflows"] },
];

const lifecycleNodes = [
  { icon: "📥", title: "Submitted", desc: "Client or monitoring system creates ticket" },
  { icon: "🔍", title: "Triaged", desc: "Priority and category auto-assigned by rules" },
  { icon: "👤", title: "Assigned", desc: "Routed to correct technician or queue" },
  { icon: "🔧", title: "Working", desc: "Active resolution with time tracked" },
  { icon: "✅", title: "Resolved", desc: "Solution documented in knowledge base" },
  { icon: "⭐", title: "Reviewed", desc: "Client satisfaction survey sent" },
];

const integrations = [
  { icon: "🔷", name: "Microsoft 365" }, { icon: "🔴", name: "Azure AD" }, { icon: "🟢", name: "Bitdefender" },
  { icon: "🟡", name: "Kaseya" }, { icon: "🔵", name: "Salesforce" }, { icon: "🟣", name: "Datto" },
  { icon: "⚫", name: "SentinelOne" }, { icon: "🟠", name: "Webroot" }, { icon: "🔶", name: "Acronis" },
  { icon: "⚪", name: "Veeam" }, { icon: "🟤", name: "IT Glue" }, { icon: "🟦", name: "QuickBooks" },
];

const industries = [
  { icon: "🏢", name: "SMB MSPs", desc: "PSA + RMM core stack for MSPs managing 50–500 endpoints — the operational backbone of the small-to-midsize managed service provider." },
  { icon: "🏥", name: "Healthcare MSPs", desc: "HIPAA-aligned security stack, backup monitoring, and compliance dashboards for MSPs serving medical practices and healthcare organizations." },
  { icon: "🏦", name: "Financial MSPs", desc: "SEC/FINRA compliance monitoring, change management workflows, and cyber insurance evidence documentation for MSPs in financial services." },
  { icon: "🏛️", name: "Government MSPs", desc: "FedRAMP-adjacent security controls, CMMC compliance workflows, and multi-tenant management for MSPs serving government contractors." },
  { icon: "🎓", name: "Education", desc: "Student device management, teacher endpoint monitoring, and Chromebook/iPad fleet management through ConnectWise Automate policy groups." },
  { icon: "⚖️", name: "Legal MSPs", desc: "Document management system integration, change-controlled environments, and privileged access monitoring for law firm IT management." },
  { icon: "🏭", name: "Manufacturing", desc: "OT asset monitoring, PLC health checks through custom monitors, and Industrial IoT device management within ConnectWise Automate." },
  { icon: "🛒", name: "Retail", desc: "POS endpoint monitoring, multi-site patch compliance, and network device health tracking across distributed retail and franchise environments." },
];

const certs = [
  { icon: "🏆", name: "ConnectWise Partner", desc: "Authorized ConnectWise resale and managed services delivery partner" },
  { icon: "⚙️", name: "Automate Specialist", desc: "ConnectWise Automate implementation and scripting certified engineers" },
  { icon: "📋", name: "Manage PSA Certified", desc: "ConnectWise Manage PSA deployment and workflow configuration specialists" },
  { icon: "🔐", name: "Security Certified", desc: "ConnectWise cybersecurity and SIEM managed services certified" },
];

const faqData = [
  { q: "What is the difference between ConnectWise Manage and ConnectWise Automate?", a: "ConnectWise Manage is a Professional Services Automation (PSA) platform — the business management system for IT service providers. It handles service tickets, client contracts, billing, time tracking, project management, purchasing, and reporting. Everything related to running the business side of an MSP flows through ConnectWise Manage. ConnectWise Automate is a Remote Monitoring and Management (RMM) platform — the technical tool for monitoring and managing endpoint devices. It monitors servers, workstations, network equipment, and cloud services; deploys patches and software; runs automation scripts; and provides remote control access. The two are deeply integrated — Automate monitoring alerts automatically create Manage tickets, and time entered in the Manage portal bills against the correct client contract. ALG Data Guard implements both platforms together for comprehensive MSP operations." },
  { q: "How long does a ConnectWise Manage and Automate implementation take?", a: "A standard ConnectWise Manage implementation covering ticket management, contract billing, client portal, and basic reporting takes 6–10 weeks. Adding ConnectWise Automate with monitoring policy deployment, patch management configuration, and initial automation scripts extends the timeline to 10–16 weeks. The most time-intensive aspects are data migration from your existing PSA/RMM system, configuring contract structures that match your actual service agreements, and building monitoring policies appropriate for your client mix. ALG Data Guard uses a phased implementation approach — core platform configuration first, then automation and customization in subsequent phases — so you see value quickly while deeper capabilities are built in parallel." },
  { q: "Can ConnectWise integrate with our existing security and backup vendors?", a: "ConnectWise has an extensive integration ecosystem with 300+ supported integrations. Key security integrations include Bitdefender GravityZone, SentinelOne, Malwarebytes, Webroot, and CrowdStrike — surfacing endpoint security status directly in Automate monitoring and Manage tickets. Backup integrations cover Veeam, Datto, Acronis, and Barracuda — monitoring backup job success and creating alerts for failed backups automatically. Identity integrations include Microsoft 365/Azure AD (synchronizing client tenants), and documentation integrations with IT Glue and Hudu keep documentation automatically updated from Automate discovery. ALG Data Guard integrates ConnectWise with your existing vendor stack as part of the implementation — including custom integrations using the ConnectWise REST API when needed." },
  { q: "What is ConnectWise Command and how does it differ from ConnectWise Automate?", a: "ConnectWise Command is ConnectWise's cloud-native RMM platform — simpler to deploy and manage than ConnectWise Automate, but with less automation depth. Command includes managed NOC services operated by ConnectWise staff, meaning alerts can be routed to ConnectWise's NOC for after-hours or overflow support. This makes Command attractive for smaller MSPs without dedicated NOC staff. ConnectWise Automate is the more powerful, on-premises-server-based RMM with deeper scripting capabilities, more monitoring options, and better support for complex enterprise environments. The choice between them depends on your MSP's size, technical depth, and whether you want to operate your own NOC or leverage ConnectWise's managed NOC services. ALG Data Guard helps MSPs evaluate which platform aligns with their business model and growth trajectory." },
  { q: "How does ALG Data Guard help MSPs optimize their ConnectWise environment?", a: "ALG Data Guard provides ongoing ConnectWise optimization services beyond initial implementation: ConnectWise Automate script library review and efficiency improvements, monitoring policy tuning to reduce alert fatigue and false positives, Manage workflow automation for common repetitive processes, contract profitability analysis identifying underpriced agreements, technician utilization analysis identifying scheduling inefficiencies, custom reporting and dashboard development for business intelligence, integration maintenance and troubleshooting, and training for new technicians joining your team. We also assist with ConnectWise platform version upgrades and migration projects when MSPs transition between ConnectWise products." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function ConnectWise() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(25000, 2200, metricsInView);
  const c2 = useCounter(500000, 2500, metricsInView);
  const c3 = useCounter(40, 2000, metricsInView);
  const c4 = useCounter(300, 2200, metricsInView);

  return (
    <div className="cw-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={connectwiseLogo} alt="ConnectWise" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              MSP Platform & Automation Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × ConnectWise</h1>
            <p className="pd-identity-desc">
              ConnectWise is the world's leading IT business management platform — powering 25,000+ managed service providers with its integrated PSA, RMM, cybersecurity, and business intelligence software. The ConnectWise platform automates the full MSP service lifecycle from endpoint monitoring through billing, enabling IT service providers to scale without proportionally scaling headcount. ALG Data Guard is an authorized ConnectWise partner implementing and managing the platform for MSPs and enterprise IT teams.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="cw-hero">
        <div className="cw-hero__noise" aria-hidden="true" />
        <div className="cw-hero__glow-l" aria-hidden="true" />
        <div className="cw-hero__glow-r" aria-hidden="true" />
        <div className="cw-hero__inner">
          <div>
            <div className="cw-pill cw-pill--dark"><span className="cw-dot" /> ConnectWise Authorized Partner — PSA + RMM</div>
            <h2 className="cw-hero__title">
              Run Your Entire MSP<br />from <em>One Platform</em>.
            </h2>
            <p className="cw-hero__body">
              ConnectWise unifies PSA, RMM, cybersecurity, and business intelligence in one platform — automating the ticket-to-invoice lifecycle so your technicians spend more time resolving issues and less time doing admin. ALG Data Guard implements and optimizes ConnectWise for MSPs ready to scale.
            </p>
            <div className="cw-hero__actions">
              <button className="cw-btn cw-btn--blue" onClick={() => navigate("/contact")}>
                Implement ConnectWise <ArrowIcon />
              </button>
              <button className="cw-btn cw-btn--ghost" onClick={() => navigate("/contact")}>
                Optimize My PSA/RMM
              </button>
            </div>
            <div className="cw-hero__kpis">
              <div><div className="cw-hero__kpi-val">25,000+</div><div className="cw-hero__kpi-label">MSP Customers</div></div>
              <div><div className="cw-hero__kpi-val">500K+</div><div className="cw-hero__kpi-label">Endpoints Managed</div></div>
              <div><div className="cw-hero__kpi-val">Partner</div><div className="cw-hero__kpi-label">ALG Data Guard Tier</div></div>
            </div>
          </div>

          {/* Dashboard Widget Cluster */}
          <div className="cw-dashboard-cluster">
            <div className="cw-db-card">
              <div className="cw-db-card__header">
                <span className="cw-db-card__label">Operations Dashboard — Live</span>
                <span className="cw-db-card__live cw-db-card__live--green">● Active</span>
              </div>
              <div className="cw-stat-row">
                <div className="cw-stat-box cw-stat-box--g"><div className="cw-stat-box__val">847</div><div className="cw-stat-box__key">Open Tickets</div></div>
                <div className="cw-stat-box cw-stat-box--b"><div className="cw-stat-box__val">98.7%</div><div className="cw-stat-box__key">SLA Met</div></div>
                <div className="cw-stat-box cw-stat-box--o"><div className="cw-stat-box__val">12</div><div className="cw-stat-box__key">P1 Alerts</div></div>
                <div className="cw-stat-box cw-stat-box--r"><div className="cw-stat-box__val">4,219</div><div className="cw-stat-box__key">Endpoints</div></div>
              </div>
            </div>
            <div className="cw-db-card">
              <div className="cw-db-card__header">
                <span className="cw-db-card__label">Priority Queue</span>
                <span className="cw-db-card__live cw-db-card__live--blue">Automate Active</span>
              </div>
              <div className="cw-ticket-list">
                {[
                  { prio: "P1", cls: "p1", icon: "🔴", text: "SQL Server unresponsive — Client A Prod", age: "4m" },
                  { prio: "P2", cls: "p2", icon: "🟡", text: "Backup failure — 3 consecutive nights", age: "2h" },
                  { prio: "P3", cls: "p3", icon: "🟢", text: "Patch deployment pending approval", age: "6h" },
                ].map(t => (
                  <div key={t.text} className="cw-ticket">
                    <div className={`cw-ticket__prio cw-ticket__prio--${t.cls}`}>{t.prio}</div>
                    <span className="cw-ticket__text">{t.icon} {t.text}</span>
                    <span className="cw-ticket__age">{t.age}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="cw-db-card">
              <div className="cw-db-card__header">
                <span className="cw-db-card__label">Automation — New Client Onboarding</span>
              </div>
              <div className="cw-auto-flow">
                {["Discovery Script ✓", "→", "Monitoring Deployed ✓", "→", "Patch Policy ✓", "→", "Contract Created 🔵"].map((n, i) =>
                  n === "→" ?
                    <span key={i} className="cw-auto-arrow">{n}</span> :
                    <span key={n} className={`cw-auto-node ${n.includes("🔵") ? "cw-auto-node--blue" : "cw-auto-node--green"}`}>{n}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pipeline ── */}
      <section className="cw-pipeline">
        <div className="cw-shell">
          <div className="cw-pipeline__head">
            <div className="cw-pill cw-pill--blue"><span className="cw-dot" /> Unified IT Lifecycle</div>
            <h2 className="cw-title">From Alert to Invoice — Fully Automated</h2>
            <p className="cw-sub" style={{ margin:"0 auto", maxWidth:"600px" }}>ConnectWise automates every step of the IT service lifecycle — eliminating manual handoffs, reducing response time, and ensuring nothing falls through the cracks.</p>
          </div>
          <div className="cw-pipeline__flow">
            {pipelineSteps.map(step => (
              <div key={step.title} className="cw-pipe-step">
                <div className="cw-pipe-step__icon">{step.icon}</div>
                <h3 className="cw-pipe-step__title">{step.title}</h3>
                <p className="cw-pipe-step__desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PSA ── */}
      <section className="cw-psa">
        <div className="cw-shell">
          <div className="cw-psa__inner">
            <div>
              <div className="cw-pill cw-pill--dark"><span className="cw-dot" /> Professional Services Automation</div>
              <h2 className="cw-psa__title">ConnectWise Manage — The Business Engine Behind Your MSP</h2>
              <p className="cw-psa__body">ConnectWise Manage is the world's most deployed PSA for managed service providers — used by 25,000+ MSPs to manage every client interaction, project, billing cycle, and business intelligence metric from one central platform. It's not a ticketing system — it's the operating system of your MSP business.</p>
              <p className="cw-psa__body">ALG Data Guard implements ConnectWise Manage with contract structures aligned to your actual pricing model, automated billing workflows, and custom dashboards that give you real-time visibility into business performance.</p>
              <div className="cw-psa__features">
                {psaFeatures.map(f => (
                  <div key={f.title} className="cw-feat">
                    <span className="cw-feat__icon">{f.icon}</span>
                    <div>
                      <div className="cw-feat__title">{f.title}</div>
                      <p className="cw-feat__desc">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="cw-btn cw-btn--blue" onClick={() => navigate("/contact")}>
                  Implement ConnectWise Manage <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80"
              alt="ConnectWise PSA dashboard"
              className="cw-psa__img"
            />
          </div>
        </div>
      </section>

      {/* ── RMM ── */}
      <section className="cw-rmm">
        <div className="cw-shell">
          <div className="cw-rmm__inner">
            <img
              src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=900&q=80"
              alt="ConnectWise Automate RMM"
              className="cw-rmm__img"
            />
            <div>
              <div className="cw-pill cw-pill--blue"><span className="cw-dot" /> Remote Monitoring & Management</div>
              <h2 className="cw-rmm__title">ConnectWise Automate — Monitor, Manage, Automate Everything</h2>
              <p className="cw-rmm__body">ConnectWise Automate is the most powerful RMM platform for MSPs — combining endpoint monitoring, remote control, patch management, and a built-in scripting engine that automates routine tasks across thousands of endpoints simultaneously. When Automate detects an issue, it can remediate automatically before the client ever notices.</p>
              <p className="cw-rmm__body">ALG Data Guard deploys ConnectWise Automate with your monitoring policies tuned to your client base, automated patch approval workflows, and custom scripts for your most common service requests.</p>
              <div className="cw-rmm__capabilities">
                {rmmCaps.map(cap => (
                  <div key={cap.title} className="cw-cap">
                    <span className="cw-cap__icon">{cap.icon}</span>
                    <div className="cw-cap__title">{cap.title}</div>
                    <p className="cw-cap__desc">{cap.desc}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="cw-btn cw-btn--blue" onClick={() => navigate("/contact")}>
                  Deploy ConnectWise Automate <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Automation Engine ── */}
      <section className="cw-automation">
        <div className="cw-shell">
          <div className="cw-automation__head">
            <div className="cw-pill cw-pill--dark"><span className="cw-dot" /> The Full Platform</div>
            <h2 className="cw-title">Every ConnectWise Tool, Deployed Together</h2>
            <p className="cw-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>ConnectWise's platform covers every operational layer of the modern MSP — from endpoint management and security to quoting, billing, and business analytics.</p>
          </div>
          <div className="cw-automation__grid">
            {autoCards.map(card => (
              <div key={card.title} className="cw-auto-card">
                <span className="cw-auto-card__icon">{card.icon}</span>
                <h3 className="cw-auto-card__title">{card.title}</h3>
                <p className="cw-auto-card__desc">{card.desc}</p>
                <div className="cw-auto-card__tags">{card.tags.map(t => <span key={t} className="cw-auto-tag">{t}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Ticket Lifecycle ── */}
      <section className="cw-lifecycle">
        <div className="cw-shell">
          <div className="cw-lifecycle__head">
            <div className="cw-pill cw-pill--blue"><span className="cw-dot" /> Ticket Lifecycle</div>
            <h2 className="cw-title">Every Ticket, Fully Tracked — From Open to Invoice</h2>
            <p className="cw-sub" style={{ margin:"0 auto", maxWidth:"600px" }}>ConnectWise Manage tracks every ticket through its complete lifecycle — with SLA clocks, automated status updates, and time-to-bill automation at every stage.</p>
          </div>
          <div className="cw-lifecycle__track">
            {lifecycleNodes.map(node => (
              <div key={node.title} className="cw-lifecycle-node">
                <div className="cw-lifecycle-node__dot">{node.icon}</div>
                <h3 className="cw-lifecycle-node__title">{node.title}</h3>
                <p className="cw-lifecycle-node__desc">{node.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Integrations ── */}
      <section className="cw-integrations">
        <div className="cw-shell">
          <div className="cw-integrations__head">
            <div className="cw-pill cw-pill--blue"><span className="cw-dot" /> Integration Ecosystem</div>
            <h2 className="cw-title">ConnectWise Connects to Your Entire Stack</h2>
            <p className="cw-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>ConnectWise's 300+ integrations connect your PSA and RMM to every tool in your MSP tech stack — security vendors, backup platforms, documentation tools, and accounting systems.</p>
          </div>
          <div className="cw-integrations__grid">
            {integrations.map(int => (
              <div key={int.name} className="cw-int-chip">
                <span className="cw-int-chip__icon">{int.icon}</span>
                {int.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="cw-industries">
        <div className="cw-shell">
          <div className="cw-industries__head">
            <div className="cw-pill cw-pill--blue"><span className="cw-dot" /> Industry Solutions</div>
            <h2 className="cw-title">ConnectWise for Every MSP Vertical</h2>
            <p className="cw-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>ConnectWise supports MSPs serving every client industry — with industry-specific automation templates, compliance dashboards, and monitoring policies.</p>
          </div>
          <div className="cw-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="cw-industry-card">
                <span className="cw-industry-card__icon">{ind.icon}</span>
                <h3 className="cw-industry-card__name">{ind.name}</h3>
                <p className="cw-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="cw-metrics" ref={metricsRef}>
        <div className="cw-shell">
          <div className="cw-metrics__inner">
            <div><div className="cw-metric__val">{c1.toLocaleString()}+</div><div className="cw-metric__label">MSP Customers Globally</div></div>
            <div><div className="cw-metric__val">{c2.toLocaleString()}+</div><div className="cw-metric__label">Endpoints Under Management</div></div>
            <div><div className="cw-metric__val">{c3}%</div><div className="cw-metric__label">Avg Ticket Volume Automated</div></div>
            <div><div className="cw-metric__val">{c4}+</div><div className="cw-metric__label">Platform Integrations</div></div>
          </div>
        </div>
      </section>

      {/* ── Certs ── */}
      <section className="cw-certs">
        <div className="cw-shell">
          <div className="cw-certs__head">
            <div className="cw-pill cw-pill--blue"><span className="cw-dot" /> Partner Credentials</div>
            <h2 className="cw-title">Certified ConnectWise Partner</h2>
            <p className="cw-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>ALG Data Guard's ConnectWise certifications validate our platform depth across PSA, RMM, and security operations.</p>
          </div>
          <div className="cw-certs__grid">
            {certs.map(c => (
              <div key={c.name} className="cw-cert-card">
                <span className="cw-cert-card__icon">{c.icon}</span>
                <div className="cw-cert-card__name">{c.name}</div>
                <p className="cw-cert-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="cw-faq">
        <div className="cw-shell">
          <div className="cw-faq__head">
            <div className="cw-pill cw-pill--blue" style={{ margin:"0 auto 1rem" }}><span className="cw-dot" /> FAQs</div>
            <h2 className="cw-title">ConnectWise Questions, Answered</h2>
            <p className="cw-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Expert answers about the ConnectWise platform and ALG Data Guard's implementation services.</p>
          </div>
          <div className="cw-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`cw-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="cw-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="cw-faq-item__q">{faq.q}</span>
                  <span className="cw-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="cw-faq-item__answer"><div className="cw-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cw-cta">
        <div className="cw-cta__inner">
          <div className="cw-pill cw-pill--dark" style={{ margin:"0 auto 1.2rem" }}><span className="cw-dot" /> Get Started</div>
          <h2 className="cw-cta__title">Build the MSP Operations Platform You Deserve</h2>
          <p className="cw-cta__sub">Whether you're implementing ConnectWise for the first time, migrating from a competing PSA/RMM, or optimizing an existing deployment — ALG Data Guard's certified ConnectWise team delivers platform implementations that scale with your business.</p>
          <div className="cw-cta__actions">
            <button className="cw-btn cw-btn--blue" onClick={() => navigate("/contact")}>
              Start My ConnectWise Implementation <ArrowIcon />
            </button>
            <button className="cw-btn cw-btn--ghost" onClick={() => navigate("/contact")}>
              Optimize My Existing PSA/RMM
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
